// src/app/app.routes.ts
import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { PermissionGuard } from 'app/core/auth/guards/permission.guard';
import { ReativacaoGuard } from 'app/core/auth/guards/reativacao.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { HomeComponent } from 'app/pages/home/home.component';
import { MinhaAssinaturaComponent } from 'app/pages/administrative/minha-assinatura/minha-assinatura.component';
import { ComunicadosComponent } from 'app/pages/comunicados/comunicados.component';
import { EdducaiComponent } from 'app/pages/edducai/edducai.component';
import { NewsletterComponent } from 'app/pages/newsletter/newsletter.component';
import { ComissaoSearchComponent } from './pages/comissao/comissao-search/comissao-search.component';
import { FeedbackComponent } from './pages/feedback/feedback.component';
import { ModuloDashcardsComponent } from './pages/trilha-do-sucesso/modulo-dashcards/modulo-dashcards.component';
import { AulaDashcardsComponent } from './pages/trilha-do-sucesso/aula-dashcards/aula-dashcards.component';
import { BadgesComponent } from './pages/badges/badges.component';
import { CertificadosComponent } from './pages/certificados/certificados.component';
import { RankingComponent } from './pages/ranking/ranking.component';
import { AuthGuardService } from './shared/services/core';
import { AulasAoVivoComponent } from './pages/aulas-ao-vivo/aulas-ao-vivo.component';
import { PerfilEnum } from './shared/models/core/security/perfil.enum';

export const appRoutes: Route[] = [
  // BLOCO PÚBLICO: landing
  {
    path: '',
    component: LayoutComponent,
    data: { layout: 'empty' },
    loadChildren: () =>
      import('./pages/landing-page/landing-page.routes').then(m => m.LANDING_ROUTES),
  },

  // BLOCO PÚBLICO: checkout
  {
    path: 'checkout',
    component: LayoutComponent,
    data: { layout: 'empty' },
    loadChildren: () =>
      import('./pages/checkout/checkout.routes').then(m => m.CHECKOUT_ROUTES),
  },

  // BLOCO PÚBLICO: cadastro via parceria comercial (link de convite enviado por e-mail)
  {
    path: 'cadastro/parceria',
    component: LayoutComponent,
    data: { layout: 'empty' },
    loadChildren: () =>
      import('./pages/cadastro-parceria/cadastro-parceria.routes').then(m => m.CADASTRO_PARCERIA_ROUTES),
  },

  // BLOCO SEMI-PÚBLICO: reativação (exige JWT de escopo "reativacao")
  {
    path: 'reativacao',
    canActivate: [ReativacaoGuard],
    data: { layout: 'empty' },
    loadComponent: () => import('./pages/reativacao/reativacao.component').then(m => m.ReativacaoComponent),
  },

  // BLOCO PÚBLICO: política de privacidade
  {
    path: 'politica-privacidade',
    loadComponent: () =>
      import('./pages/politica-privacidade/politica-privacidade.component').then(m => m.PoliticaPrivacidadeComponent),
  },

  // BLOCO PÚBLICO: auth (login/forgot, etc.)
  {
    path: '',
    component: LayoutComponent,
    data: { layout: 'empty' },
    loadChildren: () =>
      import('./pages/auth/auth.routes').then(m => m.AUTH_ROUTES),
    canActivateChild: [NoAuthGuard], // redireciona logado -> /inicio
  },

  // BLOCO PRIVADO
  {
    path: '',
    component: LayoutComponent,
    resolve: { initialData: initialDataResolver },
    canActivateChild: [AuthGuardService], // aqui pode chamar backend (check)
    children: [
      // Rotas exclusivas ADM_SISTEMA
      {
        path: 'imobiliaria',
        title: "Cad. Imobiliária - Edduca Imob",
        loadChildren: () => import('./pages/administrative/admin.routes'),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA] }
      },
      {
        path: 'trilha',
        title: "Cad. Trilha - Edduca Imob",
        loadChildren: () => import('./pages/administrative/admin.routes'),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA] }
      },
      {
        path: 'modulo',
        title: "Cad. Módulo - Edduca Imob",
        loadChildren: () => import('./pages/administrative/admin.routes'),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA] }
      },
      {
        path: 'aula',
        title: "Cad. Aula - Edduca Imob",
        loadChildren: () => import('./pages/administrative/admin.routes'),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA] }
      },
      {
        path: 'cupom',
        title: "Gestão de Cupons - Edduca Imob",
        loadChildren: () => import('./pages/administrative/admin.routes'),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA] }
      },
      {
        path: 'banner',
        title: "Cad. Banner - Edduca Imob",
        loadChildren: () => import('./pages/administrative/admin.routes'),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA] }
      },
      {
        path: 'parceria-comercial',
        title: "Parcerias Comerciais - Edduca Imob",
        loadChildren: () => import('./pages/administrative/admin.routes'),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_PARCERIA] }
      },

      // Rotas ADM_SISTEMA, ADM_IMOBILIARIA e PARCEIRO
      {
        path: 'usuario',
        title: "Cad. Usuario - Edduca Imob",
        loadChildren: () => import('./pages/administrative/admin.routes'),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.PARCEIRO] }
      },
      {
        path: 'dashboard',
        title: 'Dashboard - Edduca Imob',
        loadChildren: () => import('./pages/dashboard/dashboard.routes').then(m => m.DASHBOARD_ROUTES),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.PARCEIRO] }
      },
      {
        path: 'minha-assinatura',
        title: 'Assinatura - Edduca Imob',
        component: MinhaAssinaturaComponent,
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA] }
      },
      {
        path: 'minha-imobiliaria',
        title: 'Minha Imobiliária - Edduca Imob',
        loadComponent: () => import('./pages/minha-imobiliaria/minha-imobiliaria.component').then(m => m.MinhaImobiliariaComponent),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_IMOBILIARIA] }
      },
      {
        path: 'parametros',
        title: 'Parâmetros do Sistema - Edduca Imob',
        loadComponent: () => import('./pages/administrative/parametros/parametros.component').then(m => m.ParametrosComponent),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA] }
      },
      {
        path: 'solicitacoes-enterprise',
        title: 'Solicitações Enterprise - Edduca Imob',
        loadComponent: () => import('./pages/admin/solicitacoes-enterprise/solicitacoes-enterprise.component').then(m => m.SolicitacoesEnterpriseComponent),
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA] }
      },
      {
        path: 'comissao',
        title: 'Comissão - Edduca Imob',
        component: ComissaoSearchComponent,
        canActivate: [PermissionGuard],
        data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.PARCEIRO] }
      },

      // Rotas acessíveis a todos os perfis autenticados
      { path: 'academia-edduca', title: "Academia Edduca - Edduca Imob", loadChildren: () => import('./pages/trilha-do-sucesso/trilha-do-sucesso.routes') },
      { path: 'modulo/:id', title: "Academia Edduca - Edduca Imob", component: ModuloDashcardsComponent },
      { path: 'aula/:id', title: "Academia Edduca - Edduca Imob", component: AulaDashcardsComponent },
      { path: 'badges', title: "Badges - Edduca Imob", component: BadgesComponent },
      { path: 'certificados', title: "Certificados - Edduca Imob", component: CertificadosComponent },
      {
        path: 'inicio',
        title: 'Home - Edduca Imob',
        component: HomeComponent
      },
      {
        path: 'newsletter',
        title: 'Newsletter - Edduca Imob',
        component: NewsletterComponent
      },
      {
        path: 'comunicados',
        title: 'Comunicados - Edduca Imob',
        component: ComunicadosComponent
      },
      {
        path: 'edducai',
        title: 'EdducaAI - Edduca Imob',
        component: EdducaiComponent
      },
      {
        path: 'feedbacks',
        title: 'Feedbacks - Edduca Imob',
        component: FeedbackComponent
      },
      {
        path: 'ranking',
        title: 'Ranking - Edduca Imob',
        component: RankingComponent
      },
      {
        path: 'aulas-ao-vivo',
        title: 'Aulas ao vivo - Edduca Imob',
        component: AulasAoVivoComponent
      },
      {
        path: 'meu-perfil',
        title: 'Meu Perfil - Edduca Imob',
        loadComponent: () => import('./pages/meu-perfil/meu-perfil.component').then(m => m.MeuPerfilComponent)
      }
    ]
  },

  // fallback
  { path: '**', redirectTo: '' },
];
